import { useCreatePartCategories } from '@partful/pages/manage-product/parts-categories/lib/hooks/useCreatePartCategories';
import { useCreatePartCategoriesJob } from '@partful/pages/manage-product/parts-categories/lib/hooks/useCreatePartCategoriesJob';
import { useQueryParams } from '@partful/shared/lib/hooks/useQueryParams';
import { useConfiguration } from '@providers/Configuration';
import { useTenant } from '@providers/Tenant';
import { CategoryNode } from '@services/productInventory';
import { FlatNode } from './useFlatTree';

export interface PUTPartCategory {
  relatedHierarchyPID: string; // assy1PID
  epcHierarchyPosition: string[]; // [cat1, cat2]
  combineAssemblyCategory: boolean; // false for categories, true for cassys
}

interface SavePartCategoriesProps {
  newTableData?: FlatNode<CategoryNode>[];
}

/* istanbul ignore next - this hook will become obsolete when the new parts categories backend is refactored so don't need full coverage */
export const useSavePartsCategories = ({ newTableData }: SavePartCategoriesProps) => {
  const { accountId } = useTenant();
  const { configurationPID } = useQueryParams();
  const { parentPID: productPID } = useConfiguration();

  const { mutateAsync: createPartCategoriesJob, isLoading, isSuccess } = useCreatePartCategoriesJob();
  const { mutateAsync: createPartCategories } = useCreatePartCategories();

  const isProcessing = isLoading || isSuccess;

  const getEPCHierarchyPosition = (rowData: FlatNode<CategoryNode>) => {
    const buildParentRecursive = (parents: string[], parent?: string): string[] => {
      if (!parent) return parents;

      const parentNode = newTableData?.find((node) => node.PID === parent);
      if (!parentNode) return parents;

      return buildParentRecursive([parentNode.name, ...parents], parentNode.parent);
    };

    const categoryName = rowData.isCategory ? [rowData.name] : [];

    return buildParentRecursive([...categoryName], rowData.parent);
  };

  const savePartCategories = async (): Promise<void> => {
    const { runID } = await createPartCategoriesJob({ configurationPID, accountId });

    const newPartCategories = newTableData
      ?.map((rowData) => {
        const isCassy = rowData.itemType === 'Assembly' && rowData.isCategory;

        const epcHierarchyPosition = getEPCHierarchyPosition(rowData);
        const combineAssemblyCategory = Boolean(isCassy || rowData.isCategory);

        if (rowData.itemType !== 'Category' && epcHierarchyPosition.length > 0) {
          return {
            relatedHierarchyPID: rowData.PID,
            epcHierarchyPosition,
            combineAssemblyCategory,
          } as PUTPartCategory;
        }
        return null;
      })
      .filter(Boolean) as PUTPartCategory[];

    await createPartCategories({
      productPID,
      configurationPID,
      accountId,
      partCategoriesJobRunID: runID,
      categories: newPartCategories ?? [],
    });
  };

  return { savePartCategories, isProcessing };
};
